import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { DocumentTemplate, DocumentTemplateType, ApiResponse } from '@/types'
import { removeListQueries, invalidateDetailQueries } from '@/lib/queryHelpers'

// Query keys
export const templateKeys = {
  all: ['documentTemplates'] as const,
  lists: () => [...templateKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...templateKeys.lists(), filters] as const,
  details: () => [...templateKeys.all, 'detail'] as const,
  detail: (id: number) => [...templateKeys.details(), id] as const,
}

// Fetch templates
export function useDocumentTemplates(page = 1, documentType?: string) {
  return useQuery({
    queryKey: templateKeys.list({ page, documentType }),
    queryFn: async () => {
      const params: Record<string, unknown> = { page }
      if (documentType) params.document_type = documentType
      const { data } = await api.get<ApiResponse<DocumentTemplate[]>>('/api/document_templates', {
        params,
      })
      return data
    },
  })
}

// Fetch single template
export function useDocumentTemplate(id: number) {
  return useQuery({
    queryKey: templateKeys.detail(id),
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<DocumentTemplate>>(`/api/document_templates/${id}`)
      return data.data
    },
    enabled: !!id,
  })
}

// Create template
interface CreateTemplateInput {
  name: string
  document_type: DocumentTemplateType
  template: Record<string, unknown>
}

export function useCreateDocumentTemplate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: CreateTemplateInput) => {
      const { data } = await api.post<ApiResponse<DocumentTemplate>>('/api/document_templates', {
        document_template: input,
      })
      return data.data
    },
    onSuccess: () => {
      removeListQueries(queryClient, templateKeys.lists())
    },
  })
}

// Update template
interface UpdateTemplateInput {
  id: number
  name?: string
  document_type?: DocumentTemplateType
  template?: Record<string, unknown>
}

export function useUpdateDocumentTemplate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...input }: UpdateTemplateInput) => {
      const { data } = await api.patch<ApiResponse<DocumentTemplate>>(
        `/api/document_templates/${id}`,
        {
          document_template: input,
        }
      )
      return data.data
    },
    onSuccess: (data) => {
      removeListQueries(queryClient, templateKeys.lists())
      invalidateDetailQueries(queryClient, templateKeys.detail(data.id))
    },
  })
}

// Delete template
export function useDeleteDocumentTemplate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/document_templates/${id}`)
      return id
    },
    onSuccess: () => {
      removeListQueries(queryClient, templateKeys.lists())
    },
  })
}
